import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
    switchMap,
    mergeMap,
    exhaustMap,
    catchError,
    concatMap,
    tap,
    filter,
    withLatestFrom
} from 'rxjs/operators';
import { CompanySettingService } from '../company-setting.service';
import { LoggingService } from '../../shared/services/logging/logging.service';
import * as fromCompanySettings from './company-setting.actions';
import * as selectors from './company-setting.reducer';
import * as fromShared from '../../shared';
import { ResponseStatus } from '../../shared';
import { Store, select } from '@ngrx/store';
import { CompanySetting } from './company-setting.model';

@Injectable()
export class CompanySettingEffects {
    constructor(
        private log: LoggingService,
        private actions$: Actions,
        private service: CompanySettingService,
        private store: Store
    ) {
        this.log.verbose({
            type: 'CompanySettingEffects::'
        });
    }

    getCompanySettings$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromCompanySettings.getCompanySettings),
            tap(a =>
                this.log.verbose({
                    type: 'CompanySettingEffects:getCompanySettings$',
                    a
                })
            ),
            switchMap(() =>
                this.service.get().pipe(
                    concatMap(companySettings =>
                        companySettings
                            ? [
                                  fromCompanySettings.clearCompanySettings(),
                                  fromCompanySettings.loadCompanySettings({
                                      companySettings
                                  })
                              ]
                            : [
                                  fromShared.Errors.handleNullDataError(
                                      companySettings,
                                      'Company Settings'
                                  )
                              ]
                    ),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        )
    );
    getCompanySetting$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromCompanySettings.getCompanySetting),
            tap(a =>
                this.log.verbose({
                    type: 'CompanySettingEffects:getCompanySetting$',
                    a
                })
            ),
            //TODO: shouldn't this be switchMap?
            mergeMap(a =>
                this.service.getSetting(a.SettingType).pipe(
                    concatMap(companySettings =>
                        companySettings
                            ? [
                                  fromCompanySettings.upsertCompanySettings({
                                      companySettings
                                  })
                              ]
                            : [
                                  fromShared.Errors.handleNullDataError(
                                      companySettings,
                                      'Company Settings'
                                  )
                              ]
                    ),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        )
    );
    putCompanySetting$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(fromCompanySettings.putCompanySettings),
            tap(a => this.log.verbose({ type: 'putCompanySetting$', a })),
            switchMap(a =>
                this.service.put(a.settings).pipe(
                    withLatestFrom(
                        this.store.pipe(
                            select(
                                selectors.getSetting(
                                    a.settings[0].SettingType as string
                                )
                            )
                        )
                    ),
                    concatMap(([resp, old]) => {
                        this.log.verbose({
                            type: 'putCompanySetting$',
                            resp,
                            old
                        });
                        const status = fromShared.Errors.MapErrorsToStatus({
                            ...resp,
                            id: old.SettingType,
                            processId: fromShared.ProcessEnum.CompanySetting,
                            Items:
                                resp.Items && resp.Items.length > 0
                                    ? resp.Items.map(x => ({
                                          ...x,
                                          id: x.SettingType
                                      }))
                                    : (undefined as any)
                        });
                        return fromShared.Errors.IsSuccessStatus(
                            resp.StatusCode
                        )
                            ? [
                                  fromCompanySettings.upsertCompanySettings({
                                      companySettings: resp.Items
                                      //   .filter(
                                      //       x => x.CtrlType
                                      //   )
                                  }),
                                  new fromShared.Errors.RemoveErrors(
                                      resp.Items.filter(
                                          x =>
                                              x.Status.StatusCode ===
                                              ResponseStatus.Success
                                      ).map(x => x.CatID.toString())
                                  )
                              ]
                            : [
                                  new fromShared.Errors.SetErrors(status),
                                  fromCompanySettings.upsertCompanySettings({
                                      companySettings:
                                          resp.Items && resp.Items.length > 0
                                              ? resp.Items
                                              : [
                                                    {
                                                        ...old,
                                                        Status: status.find(
                                                            x =>
                                                                x.MsgEntityID ===
                                                                old.SettingType
                                                        )
                                                    }
                                                ]
                                  })
                              ];
                    }),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        );
    });
    // TODO: remove hard coding to retrieve all on FYStartDate
    // careful this doesn't trigger a recursive loop, it doesn't right now
    upsertCompanySettingsFYS$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(fromCompanySettings.upsertCompanySettings),
            tap(action =>
                this.log.verbose({
                    type: 'CompanySettingEffects:upsertCompanySettingsFYS$',
                    a: action
                })
            ),
            filter(a =>
                a.companySettings
                    .map(x => x.SettingType)
                    .includes('FYStartDate')
            ),
            tap(action =>
                this.log.verbose({
                    type: 'CompanySettingEffects:upsertCompanySettingsFYS$:filter',
                    a: action
                })
            ),
            switchMap(() => [fromCompanySettings.getCompanySettings()])
        );
    });
}
